import type { RaiseBoardGlobalApplication } from "@/api/raise-board-api";
import { getStageText, GICSSubIndustries } from "@/lib/utils";

/*
 * How a raise-board deal's display values are resolved from the application
 * document, and the filter values derived from them.
 *
 * Shared by the global board, every lookbook, and the card they both render.
 * Deal fields live in several places at once — some on the application, some
 * on the populated company, some inside the `deal_info` array — so every page
 * has to agree on the precedence or two surfaces will disagree about the same
 * deal.
 */

export type Application = RaiseBoardGlobalApplication;

export function getCompany(application: Application) {
  const ac = application.applicant_company;
  if (!ac || typeof ac === "string") return null;
  return ac;
}

export function getApplicantCompanyId(
  application: Application
): string | undefined {
  const ac = application.applicant_company;
  const id = typeof ac === "string" ? ac : ac?._id;
  return id ? String(id) : undefined;
}

export function getSource(application: Application): {
  name?: string;
  slug?: string;
  enabled?: boolean;
} {
  const p = application.pertains_to_opportunity;
  if (!p || typeof p === "string") return {};
  return {
    name: p.opportunity_name,
    slug: p.portal_slug,
    enabled: p.raise_board_enabled,
  };
}

// Resolve GICS sub-industry codes on the deal to readable names (taxonomy lives
// client-side); returns all resolved names.
function resolveGicsNames(application: Application): string[] {
  const field = application.deal_info?.find(
    (f) => f.field_name === "sub_industries" && f.field_state === "approved"
  );
  if (!field?.field_value) return [];
  const codes = Array.isArray(field.field_value)
    ? field.field_value
    : [field.field_value as string];
  return codes
    .map((c) => GICSSubIndustries.find((si) => si.code === String(c))?.name)
    .filter((n): n is string => !!n);
}

function toArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

// business_model / revenue_model live on either the application or the company
// doc depending on the deal; prefer the application value, fall back to the
// company's so the field isn't lost on the board.
function pickList(
  appValue: string | string[] | undefined,
  companyValue: string | string[] | undefined
): string[] {
  const fromApp = toArray(appValue);
  return fromApp.length ? fromApp : toArray(companyValue);
}

// Editable deal fields (raise amount, currency, etc.) are carried in the
// `deal_info` array keyed by field_name, not always as top-level columns.
function dealInfoStr(
  application: Application,
  fieldName: string
): string | undefined {
  const v = application.deal_info?.find(
    (f) => f.field_name === fieldName
  )?.field_value;
  if (typeof v === "string" && v.trim()) return v;
  if (Array.isArray(v) && v.length > 0) return v.filter(Boolean).join(", ");
  if (typeof v === "number") return String(v);
  return undefined;
}

function formatRaiseAmount(amount?: string, currency?: string) {
  if (!amount) return null;
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const ccy = currency?.trim();
  return ccy ? `${trimmed} ${ccy}` : trimmed;
}

// The fundraising ask shown on each card. Prefers deal_info, falls back to the
// top-level application columns. Returns null when no amount is recorded.
export function resolveRaise(application: Application): string | null {
  const amount =
    dealInfoStr(application, "attempting_to_raise") ||
    (application.attempting_to_raise ?? "").trim();
  const currency =
    dealInfoStr(application, "currency") || (application.currency ?? "").trim();
  return formatRaiseAmount(amount || undefined, currency || undefined);
}

/**
 * The raise as a number, for sorting. The stored value is free text ("2M",
 * "500k", "$1,500,000"), so this reads the digits and a k/m/b suffix; anything
 * it can't read sorts last rather than as zero.
 */
export function raiseAmountValue(application: Application): number | null {
  const raw =
    dealInfoStr(application, "attempting_to_raise") ||
    (application.attempting_to_raise ?? "").trim();
  if (!raw) return null;
  const match = raw.replace(/,/g, "").match(/(\d+(?:\.\d+)?)\s*([kmb])?/i);
  if (!match) return null;
  const n = parseFloat(match[1]);
  const scale = { k: 1e3, m: 1e6, b: 1e9 }[
    (match[2] ?? "").toLowerCase() as "k" | "m" | "b"
  ];
  return Number.isFinite(n) ? n * (scale ?? 1) : null;
}

// Every industry value a deal carries. Prefers the resolved GICS sub-industry
// names, falling back to the plain industry array when nothing is mapped. All
// of them are returned, not just the first: a company tagged with three
// sub-industries has to be findable under each one, so the Industry filter
// matches against this whole list and the dropdown is built from it.
export function resolveDisplayIndustries(application: Application): string[] {
  const company = getCompany(application);
  const gics = resolveGicsNames(application);
  if (gics.length > 0) return gics;
  const fallback = application.industry ?? company?.industry ?? [];
  return fallback.filter(Boolean);
}

export function getRowDetails(application: Application) {
  const company = getCompany(application);
  const stageValue = application.stage ?? company?.stage;
  const stageLabel = stageValue != null ? getStageText(stageValue) : null;

  const industries = resolveDisplayIndustries(application);

  const bio = company?.short_bio ?? application.short_bio;
  const dealInfoCountry = application.deal_info?.find(
    (f) => f.field_name === "address"
  )?.field_value as string | undefined;
  const country = dealInfoCountry || company?.country || application.country;

  return { company, stageLabel, industries, bio, country };
}

/**
 * The secondary facts a row or card shows beneath the essentials: how the
 * company makes money, how old it is, where it lives online, and how long it
 * has been on this board.
 */
export function getExtraDetails(application: Application) {
  const company = getCompany(application);
  const businessModels = pickList(application.business_model, company?.business_model);
  const revenueModels = pickList(application.revenue_model, company?.revenue_model);
  const foundedRaw =
    dealInfoStr(application, "year_of_inception") ||
    dealInfoStr(application, "founded") ||
    (application.year_of_inception ?? "").trim();
  const foundedMatch = foundedRaw.match(/\b(19|20)\d{2}\b/);
  const website =
    dealInfoStr(application, "company_website") ||
    application.company_website ||
    company?.company_website ||
    "";
  let websiteHost: string | null = null;
  if (website) {
    try {
      websiteHost = new URL(website.startsWith("http") ? website : `https://${website}`)
        .hostname.replace(/^www\./, "");
    } catch {
      websiteHost = null;
    }
  }
  const createdOn = (application as { created_on?: string }).created_on ?? null;
  return {
    businessModels,
    revenueModels,
    founded: foundedMatch ? foundedMatch[0] : null,
    websiteHost,
    websiteHref: websiteHost ? `https://${websiteHost}` : null,
    addedOn: createdOn,
  };
}

/** "today", "3d ago", "5w ago", "Mar 2026": short enough for a metadata run. */
export function addedAgo(iso: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  const days = Math.floor((Date.now() - then.getTime()) / 86_400_000);
  if (Number.isNaN(days) || days < 0) return null;
  if (days === 0) return "today";
  if (days < 7) return `${days}d ago`;
  if (days < 60) return `${Math.floor(days / 7)}w ago`;
  return then.toLocaleString("en", { month: "short", year: "numeric" });
}

// Per-application values backing the filters. Each is the full list the deal
// carries (same values the row renders), and matching is any-of, so selecting
// one of a company's three industries still surfaces it.
export function getFilterValues(application: Application) {
  const industries = resolveDisplayIndustries(application);
  const businessModels = pickList(
    application.business_model,
    getCompany(application)?.business_model
  );
  // An application belongs to exactly one opportunity, so source is single.
  const source = getSource(application).name;
  return { industries, businessModels, source };
}

export function uniqueSorted(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );
}

export function matchesFilter(
  appValues: string[],
  selected: string[]
): boolean {
  if (selected.length === 0) return true;
  return appValues.some((v) => selected.includes(v));
}
